import { UserRole } from '@synergy/models';
import { createSelector } from 'reselect';
import { defaultLocale } from '@synergy/config';
import { errorsToTouch } from '@synergy/utils';

import { AppState } from '../../../../../../store/models/app-state';
import { ContactPermissions } from '../models/contact-permissions';
import { ContactPermissionsFormValues } from '../models/contact-permissions-form-values';
import { PatchPermissionsRequest } from '../api/models/patch-permissions-request';

const getContactDetailPermissionsState = (state: AppState) =>
    state.contactsAndOrganizations.contacts.contactDetail.permissions;

export const contactPermissionsHasUserSelector = createSelector(
    getContactDetailPermissionsState,
    state => state.hasUser
);

export const contactPermissionsImpersonateLinksSelector = createSelector(getContactDetailPermissionsState, state => ({
    shopSwitchUserUrl: state.permissions?.shopSwitchUserUrl,
    supplierSwitchUserUrl: state.permissions?.supplierSwitchUserUrl,
    hasUser: state.hasUser,
}));

/** The person's members, to choose the ones they are shop admin of (#2109). */
export const contactShopAdminMembersSelector = createSelector(
    getContactDetailPermissionsState,
    state => state.baseline?.permissions?.shopAdminOfMembers ?? state.permissions?.shopAdminOfMembers ?? []
);

export const contactPermissionsUpdateRequestSelector = createSelector(getContactDetailPermissionsState, state => {
    const request: PatchPermissionsRequest = {
        locale: state.permissions?.locale,
        password: state.password || undefined,
        roles: state.permissions?.roles || undefined,
        // Only for a shop admin: without the role the server ends it everywhere.
        shopAdminAccountIds:
            state.permissions?.roles?.includes(UserRole.ShopAdmin) && state.permissions.shopAdminOfMembers
                ? state.permissions.shopAdminOfMembers.filter(member => member.isShopAdmin).map(member => member.id)
                : undefined,
    };
    return request;
});

/**
 * The shape the tab edits in: the roles as a box per role, and a default for
 * everything the server left empty.
 *
 * Shared with the unsaved-changes baseline (#2159) — compared against raw
 * server values, the boxes alone would read as something the user ticked.
 */
const withDefaults = (
    permissions: ContactPermissions | null,
    hasUser: boolean,
    password: string | undefined
): ContactPermissionsFormValues => {
    const activeRoles = permissions?.roles ?? [];
    const roles: Record<UserRole, boolean> = {} as Record<UserRole, boolean>;
    Object.values(UserRole).forEach(role => (roles[role] = activeRoles.includes(role)));

    return {
        locale: permissions?.locale ?? defaultLocale,
        mainEmail: permissions?.mainEmail ?? '',
        roles,
        shopAdminAccountIds: (permissions?.shopAdminOfMembers ?? [])
            .filter(member => member.isShopAdmin)
            .map(member => member.id),
        password: password ?? '',
        hasUser,
    };
};

export const contactPermissionsFormDataSelector = createSelector(getContactDetailPermissionsState, state => ({
    formValues: withDefaults(state.permissions, state.hasUser, state.password),
    errors: state.errors,
    touched: errorsToTouch(state.errors),
}));

/**
 * What the tab compares against. The password is never part of it: the server
 * never hands one back, so an empty one is what "as saved" looks like.
 */
export const contactPermissionsBaselineSelector = createSelector(getContactDetailPermissionsState, state =>
    state.baseline ? withDefaults(state.baseline.permissions, state.baseline.hasUser, '') : undefined
);
